import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Globe2 } from "lucide-react";
import Logo from "@/components/Logo";
import { EASE } from "@/components/Reveal";

function Line({ children, delay, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const LINKS = [
  { href: "#specialties", label: "What I help with", tid: "nav-link-specialties" },
  { href: "#programs", label: "Programs", tid: "nav-link-programs" },
  { href: "#about", label: "About", tid: "nav-link-about" },
  { href: "#faq", label: "FAQ", tid: "nav-link-faq" },
];

function NavA() {
  return (
    <header className="relative z-20">
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-12 pt-6 lg:pt-8 gap-6">
        <a href="#top" aria-label="Well&Truly — home" data-testid="nav-logo">
          <Logo variant="a" />
        </a>
        <nav className="hidden lg:flex items-center gap-9 text-[15px] text-white/85">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} data-testid={l.tid} className="hover:text-lime transition-colors">
              {l.label}
            </a>
          ))}
        </nav>
        <a
          href="#contact"
          data-testid="nav-cta-book"
          className="inline-flex items-center rounded-full bg-lime px-5 py-2.5 sm:px-6 sm:py-3 text-[15px] font-semibold text-navy hover:bg-cream transition-colors"
        >
          <span className="hidden sm:inline">Book a discovery call</span>
          <span className="sm:hidden">Book a call</span>
        </a>
      </div>
    </header>
  );
}

function Chip({ icon, title, sub, tid }) {
  return (
    <div
      data-testid={tid}
      className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 px-5 py-4 shadow-lg"
    >
      <div className="flex items-center gap-2 text-lime">
        {icon}
        <span className="font-semibold text-cream text-[15px]">{title}</span>
      </div>
      <p className="mt-1 text-sm text-white/70">{sub}</p>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const chipY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-[radial-gradient(115%_95%_at_50%_-10%,#6FA8FF_0%,#2E6BE6_42%,#163B94_68%,#0B132B_100%)]" />
      <div className="relative">
        <NavA />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 lg:pt-8 pb-16 lg:pb-24 grid lg:grid-cols-[1.1fr_auto_1.1fr] gap-12 lg:gap-10 items-center">
          <div className="order-2 lg:order-1">
            <h1 className="sr-only">
              Your health, guided with care — health coaching by Akanksha Maheshwari, Dubai
            </h1>
            <div
              aria-hidden="true"
              className="font-displaya font-extrabold uppercase text-lime leading-[0.9] text-[clamp(3.8rem,9vw,8.5rem)]"
            >
              <Line delay={0.15}>Your</Line>
              <Line delay={0.28}>Health,</Line>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
              className="mt-7 max-w-sm text-white/85 text-lg leading-relaxed"
            >
              Hi, I'm Akanksha — a UK-certified health coach in Dubai. I help you build simple,
              sustainable habits so you feel calm, energised and in control.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
              className="mt-8 flex flex-wrap items-center gap-5"
            >
              <a
                href="#contact"
                data-testid="hero-cta-book"
                className="group inline-flex items-center gap-3 rounded-full bg-lime pl-7 pr-2.5 py-2.5 font-semibold text-navy hover:bg-cream transition-colors"
              >
                Book a discovery call
                <span className="grid place-items-center w-10 h-10 rounded-full bg-navy text-lime transition-transform duration-300 group-hover:rotate-45">
                  <ArrowUpRight size={18} />
                </span>
              </a>
              <a
                href="#programs"
                data-testid="hero-cta-programs"
                className="text-white/90 hover:text-lime underline-offset-8 hover:underline transition-colors"
              >
                Explore programs
              </a>
            </motion.div>
          </div>

          <motion.div style={{ y: imgY }} className="order-1 lg:order-2 relative mx-auto w-[250px] sm:w-[300px] lg:w-[330px]">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 1.1, ease: EASE }}
              className="rounded-[2.75rem] overflow-hidden aspect-[3/4] ring-1 ring-white/20 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.6)]"
            >
              <img
                src="/images/akanksha_profile.jpg"
                alt="Akanksha Maheshwari, certified health coach"
                className="w-full h-full object-cover object-top"
              />
            </motion.div>
            <motion.div style={{ y: chipY }} className="hidden lg:block absolute -left-44 top-40">
              <Chip
                tid="hero-chip-certified"
                icon={<ShieldCheck size={18} />}
                title="Certified in the UK"
                sub="Evidence-informed health coaching"
              />
            </motion.div>
            <motion.div style={{ y: chipY }} className="hidden lg:block absolute -right-44 bottom-32">
              <Chip
                tid="hero-chip-dubai"
                icon={<Globe2 size={18} />}
                title="Dubai · worldwide"
                sub="Sessions in person & on video"
              />
            </motion.div>
          </motion.div>

          <div className="order-3">
            <div
              aria-hidden="true"
              className="font-displaya font-extrabold uppercase leading-[0.9] text-[clamp(3.8rem,9vw,8.5rem)]"
            >
              <Line delay={0.41} className="text-white">Guided</Line>
              <Line delay={0.54} className="text-white">With</Line>
              <Line delay={0.67} className="text-lime">Care.</Line>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:hidden gap-4">
              <Chip
                tid="hero-chip-certified-mobile"
                icon={<ShieldCheck size={18} />}
                title="Certified in the UK"
                sub="Evidence-informed coaching"
              />
              <Chip
                tid="hero-chip-dubai-mobile"
                icon={<Globe2 size={18} />}
                title="Dubai · worldwide"
                sub="In person & on video"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
